"use client";

import React, { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { MoveLeft } from "lucide-react";
import { Button } from "./ui/button";
import AR from "@/public/icon/ar.svg";
import ColorSwatch from "./ui/ColorSwatch";
import CardImage from "./ui/CardImage";
import { getProductColors, getProductValue } from "@/lib/utils/product";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { getColor } from "@/static/colors";
import { getImageUrl } from "@/lib/utils/image";

const ProductCard = ({ product, priority = false }) => {
  const colors = useMemo(() => getProductColors(product), [product]);
  const hasAR = product.ar === true || product.ar === "true" || product.ar === 1 || product.ar === "1";

  const imageUrl = useMemo(() => {
    const imgData = product.images?.[0];
    if (imgData) {
      const url = getImageUrl(imgData);
      if (url && url !== "undefined" && url !== "null") return url;
    }
    const fallbackUrl = getImageUrl(product.image || product.image_url || product.thumbnail);
    if (fallbackUrl && fallbackUrl !== "undefined" && fallbackUrl !== "null") return fallbackUrl;
    return null;
  }, [product.images, product.image, product.image_url, product.thumbnail]);

  const title = getProductValue(product.title);
  const description = getProductValue(product.description);

  return (
    <div className="w-[320px] lg:w-[395px] rounded-3xl p-4 pb-6 lg:p-6 lg:pb-8 glass-box relative mx-auto flex flex-col h-full ">
      {hasAR && (
        <Image className="absolute top-6 left-6 z-50" width={32} height={32} src={AR} alt="AR" />
      )}

      <div className="relative w-full flex justify-center flex-shrink-0 ">
        <CardImage src={imageUrl} alt={title} priority={priority} />
      </div>

      <div className="w-full flex flex-col justify-between gap-2 lg:mt-5 flex-1">
        <div>
          <h3 className="text-base lg:text-lg font-semibold mb-1 lg:mb-3 line-clamp-1">{title}</h3>
          <div
            className="text-mute text-sm line-clamp-3 mt-4 h-[60px] [&_p]:inline [&_p]:m-0"
            dangerouslySetInnerHTML={{ __html: description || "" }}
          />
        </div>

        <div className=" flex justify-between items-end">
          <Link href={`/product/${product.slug}-${product.id}`}>
            <Button className="justify-between w-fit font-semibold " variant="whiteBorder" size="lg">
              مشاهده محصول
              <MoveLeft className="text-white" />
            </Button>
          </Link>

          {colors.length > 0 && (
            <div className="flex items-center flex-wrap gap-1 mt-6">
              {colors.length > 3 && (
                <span className="text-mute text-xs lg:text-sm font-dana-medium ml-1.5">
                  +{colors.length - 3}
                </span>
              )}

              {colors.slice(0, 3).map((color) => {
                const colorInfo = getColor(color);
                return (
                  <Tooltip key={color}>
                    <TooltipTrigger asChild>
                      <div>
                        <ColorSwatch code={color} className="w-6 h-6 lg:w-8 lg:h-8" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {colorInfo?.title || color}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
